/**
 * FlowTime — звук окончания интервала и фоновый шум.
 *
 * Звуки синтезируются через Web Audio, а не грузятся файлами: набор
 * коротких mp3 весил бы больше самого приложения и требовал бы сети,
 * а инструмент должен работать офлайн.
 */

import type { FlowSettings } from './flowtime-storage';

type AlarmKind = FlowSettings['alarm'];
type AmbientKind = FlowSettings['ambient'];

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let ambient: { stop: () => void } | null = null;
let ambientKind: AmbientKind = 'none';

function audio(volume: number): AudioContext | null {
  if (typeof window === 'undefined') return null;

  ctx ??= (() => {
    const created = new AudioContext();
    master = created.createGain();
    master.gain.value = volume;
    master.connect(created.destination);
    return created;
  })();

  // Браузер держит контекст выключенным, пока не было щелчка по странице.
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Вызывается по первому действию человека: без этого звука не будет. */
export function unlockAudio(volume: number): void {
  audio(volume);
}

export function setVolume(v: number): void {
  if (ctx && master) master.gain.setTargetAtTime(v, ctx.currentTime, 0.02);
}

function tone(
  volume: number,
  opts: {
    freq: number;
    dur: number;
    type?: OscillatorType;
    gain?: number;
    at?: number;
    decay?: number;
  },
): void {
  const c = audio(volume);
  if (!c || !master) return;

  const { freq, dur, type = 'sine', gain = 0.3, at = 0, decay = 0.25 } = opts;
  const t0 = c.currentTime + at;
  const osc = c.createOscillator();
  const g = c.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.008);
  // Экспоненциальный спад звучит как затухание струны; линейный слышен
  // как резкий обрыв.
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur + decay);

  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + decay + 0.05);
}

function noise(
  volume: number,
  opts: { dur?: number; gain?: number; at?: number; freq?: number; q?: number },
): void {
  const c = audio(volume);
  if (!c || !master) return;

  const { dur = 0.12, gain = 0.25, at = 0, freq = 1200, q = 1 } = opts;
  const len = Math.ceil(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < len; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / len);

  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = freq;
  bp.Q.value = q;
  const g = c.createGain();
  g.gain.value = gain;

  src.connect(bp).connect(g).connect(master);
  src.start(c.currentTime + at);
}

/**
 * Сигнал окончания интервала.
 *
 * Колокол собран из негармоничных обертонов (2.02, 2.99, 4.21 к основному
 * тону) — именно они отличают удар по металлу от синтетического писка.
 */
export function playAlarm(kind: AlarmKind, volume: number): void {
  if (kind === 'none') return;

  switch (kind) {
    case 'bell':
      [1, 2.02, 2.99, 4.21].forEach((mult, i) => {
        tone(volume, {
          freq: 660 * mult,
          dur: 1.1 - i * 0.18,
          gain: 0.28 / (i + 1.4),
          decay: 1.4,
        });
      });
      noise(volume, { dur: 0.05, gain: 0.08, freq: 3200 });
      break;

    case 'chime':
      [784, 988, 1319].forEach((freq, i) => {
        tone(volume, { freq, dur: 0.5, gain: 0.26, type: 'triangle', at: i * 0.16, decay: 0.8 });
      });
      break;

    case 'digital':
      for (let i = 0; i < 3; i++) {
        tone(volume, {
          freq: 1046,
          dur: 0.09,
          gain: 0.22,
          type: 'square',
          at: i * 0.18,
          decay: 0.02,
        });
        tone(volume, {
          freq: 1568,
          dur: 0.09,
          gain: 0.1,
          type: 'square',
          at: i * 0.18 + 0.02,
          decay: 0.02,
        });
      }
      break;

    case 'wood':
      [0, 0.14].forEach((at) => {
        tone(volume, { freq: 420, dur: 0.06, gain: 0.3, type: 'triangle', at, decay: 0.06 });
        noise(volume, { dur: 0.05, gain: 0.18, freq: 900, q: 3, at });
      });
      break;
  }
}

/** Короткий отклик на действие: отметку задачи, добавление записи. */
export function playBlip(volume: number, up = true): void {
  tone(volume, { freq: up ? 880 : 520, dur: 0.05, gain: 0.1, decay: 0.06 });
}

export function stopAmbient(): void {
  if (ambient) {
    ambient.stop();
    ambient = null;
  }
  ambientKind = 'none';
}

function startAmbient(kind: AmbientKind, volume: number): void {
  stopAmbient();
  if (kind === 'none') return;

  const c = audio(volume);
  if (!c || !master) return;
  ambientKind = kind;

  if (kind === 'tick') {
    const id = setInterval(() => {
      tone(volume, { freq: 1500, dur: 0.012, gain: 0.05, type: 'square', decay: 0.01 });
      noise(volume, { dur: 0.02, gain: 0.03, freq: 2400, q: 4 });
    }, 1000);
    ambient = {
      stop: () => {
        clearInterval(id);
      },
    };
    return;
  }

  // Четыре секунды шума зациклены: генерировать бесконечный поток дороже,
  // а на слух повтор такой длины неразличим.
  const len = c.sampleRate * 4;
  const buf = c.createBuffer(1, len, c.sampleRate);
  const ch = buf.getChannelData(0);

  if (kind === 'brown') {
    // Броуновский шум: интеграл от белого. Низкочастотный, воспринимается
    // как ровный гул без свиста верхов.
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      ch[i] = last * 3.5;
    }
  } else {
    for (let i = 0; i < len; i++) ch[i] = Math.random() * 2 - 1;
  }

  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const filter = c.createBiquadFilter();
  const g = c.createGain();

  if (kind === 'rain') {
    filter.type = 'bandpass';
    filter.frequency.value = 1400;
    filter.Q.value = 0.4;
    g.gain.value = 0.12;

    // Медленные порывы: без них шум слышен как статичное шипение.
    const lfo = c.createOscillator();
    const lfoGain = c.createGain();
    lfo.frequency.value = 0.07;
    lfoGain.gain.value = 0.04;
    lfo.connect(lfoGain).connect(g.gain);
    lfo.start();

    src.connect(filter).connect(g).connect(master);
    src.start();
    ambient = {
      stop: () => {
        try {
          lfo.stop();
          src.stop();
        } catch {
          /* уже остановлен */
        }
      },
    };
    return;
  }

  filter.type = 'lowpass';
  filter.frequency.value = 500;
  g.gain.value = 0.16;
  src.connect(filter).connect(g).connect(master);
  src.start();
  ambient = {
    stop: () => {
      try {
        src.stop();
      } catch {
        /* уже остановлен */
      }
    },
  };
}

/** Фон звучит только во время фокуса: в перерыв тишина уместнее. */
export function syncAmbient(
  running: boolean,
  mode: string,
  kind: AmbientKind,
  volume: number,
): void {
  const want = running && mode === 'focus' ? kind : 'none';
  if (want === 'none') {
    if (ambientKind !== 'none') stopAmbient();
    return;
  }
  if (want !== ambientKind) startAmbient(want, volume);
}

export function previewAmbient(kind: AmbientKind, volume: number): void {
  startAmbient(kind, volume);
}
